import { getLogger } from '@logtape/logtape';
import { Button, Divider, Select, Switch } from '@mantine/core';
import { FileUp, QrCode } from 'lucide-react';
import { useState } from 'react';

import ConditionalTooltip from '@/components/ConditionalTooltip.tsx';
import IconButton from '@/components/IconButton.tsx';
import { AppointmentCommunicationAction } from '@/components/PatientModal/appointment-communication-action.tsx';
import DocumentExchange from '@/components/PatientModal/document-exchange.tsx';
import { InsuranceMissingAlert } from '@/components/PatientModal/insurance-missing-alert.tsx';
import Paragraph from '@/components/text/Paragraph.tsx';

import { type CheckInActionCallbacks, useCheckInActions } from '@/hooks/use-check-in-actions.ts';
import useAppointmentsForDate from '@/hooks/useAppointmentsForDate.ts';
import useCheckInData from '@/hooks/useCheckInData.ts';
import useConfig from '@/hooks/useConfig.ts';
import { useEnrichedPractitionerDataForSpecificInstance } from '@/hooks/useEnrichedPractitionerData.ts';
import usePatient from '@/hooks/usePatient.ts';
import { usePatientAppointmentForms } from '@/hooks/usePatientAppointmentForms.ts';

import { SelectionOption } from '@/models/SelectionOption.ts';
import { DailyAppointment } from '@/models/appointment/Appointment.ts';
import { InsuranceType } from '@/models/insurance.ts';

import { useAppointmentCheckinStore } from '@/stores/appointmentCheckinStore.ts';
import { useFilterStore } from '@/stores/filterStore.ts';
import { usePatientModalStore } from '@/stores/patientModalStore.ts';

import {
    getCalendarId,
    getEnrichedAppointmentType,
    getInstanceId,
    getRoomId,
    isCheckedIn,
    isCheckedOut,
    isCheckinArrived,
    isCheckinPending,
    isEternoAppointment,
} from '@/utils/appointmentUtils.ts';
import { isToday } from '@/utils/date-utils.ts';
import { filterRoomsBySelection } from '@/utils/filter-utils.ts';
import { hasFormsToFill } from '@/utils/formUtils.ts';
import { getAllowedInsuranceOptions, getInsuranceTypeFromAppointment } from '@/utils/insurance-utils.ts';
import { openAppointmentScheduler } from '@/utils/modal/appointmentSchedulerUtils.tsx';
import { openFileUploadModal } from '@/utils/modal/file-upload-modal-utils.tsx';
import { getInsuranceByKey, getUnregisteredUserId } from '@/utils/patientUtils.ts';
import { getRoomsSelection, resolveSelectedRoom } from '@/utils/roomViewUtils.ts/column.ts';

import PatientAppointmentForms from './PatientAppointmentForms.tsx';

const logger = getLogger(['component', 'patient-appointment-actions']);

interface Props {
    appointment: DailyAppointment;
}

const handleInsuranceUpdated = (updatedAppointment: DailyAppointment): void => {
    usePatientModalStore.setState((state) => ({
        appointment: state.appointment ? updatedAppointment : undefined,
    }));
};

export const PatientAppointmentActions = ({ appointment }: Props) => {
    const { mutate: mutateAppointmentsForDate } = useAppointmentsForDate({
        show_cancelled: false,
    });
    const [showCheckinButtons, setShowCheckinButtons] = useState(isToday(appointment.day));
    const [selectedRoomOverride, setSelectedRoomOverride] = useState(getRoomId(appointment));
    const [showRoomError, setShowRoomError] = useState(false);
    const [defaultSelectedInsuranceType, setDefaultSelectedInsuranceType] = useState<InsuranceType | undefined>(
        appointment.data.insurance_coverage_type
            ? (getInsuranceTypeFromAppointment(appointment) as InsuranceType)
            : undefined
    );
    const { selected_location: selectedLocation, getInstanceConfig } = useConfig();
    const config = getInstanceConfig(getInstanceId(appointment) ?? '');
    const isPatientCallSystemActive = config?.is_patient_call_system_active ?? false;
    const selectedRooms = useFilterStore((state) => state.selected_rooms);
    const currentSelectedRooms = selectedRooms[selectedLocation.key] ?? [];
    const updateRoom = usePatientModalStore((state) => state.updateRoom);

    const { patient } = usePatient({
        user_id: appointment?.patient?.pvs.user_id,
        instance_id: getInstanceId(appointment),
        pvs_provider: appointment?.pvs_provider,
    });

    const { getAppointmentCheckinData } = useCheckInData();
    const checkInActions = useCheckInActions();
    const apptCheckinData = getAppointmentCheckinData(appointment);
    const checkinStatus = apptCheckinData?.appointment?.checkin_status;
    const checkoutStatus = apptCheckinData?.appointment?.checkout_status;
    const getRoomsSelectionOptions = (): SelectionOption[] => {
        if (!config) return [];
        const filteredRooms = filterRoomsBySelection(config.rooms, currentSelectedRooms);
        return getRoomsSelection(filteredRooms, checkinStatus);
    };

    const roomsSelection = getRoomsSelectionOptions();

    const {
        appointments: appointmentsCheckedIn,
        setConfigAutoCheckin,
        is_config_auto_checkin: isConfigAutoCheckin,
    } = useAppointmentCheckinStore();
    const { is_loading: isLoadingPrac, data: enrichedPractitionerData } =
        useEnrichedPractitionerDataForSpecificInstance(getInstanceId(appointment) ?? '');

    const enrichedAppointmentType = getEnrichedAppointmentType(appointment, enrichedPractitionerData);
    const allowedInsuranceOptions = getAllowedInsuranceOptions(enrichedAppointmentType);
    const selectedInsuranceType: InsuranceType | undefined =
        allowedInsuranceOptions.length === 1 ? allowedInsuranceOptions[0].value : defaultSelectedInsuranceType;
    const isMissingInsurance =
        !selectedInsuranceType || !allowedInsuranceOptions.some((option) => option.value === selectedInsuranceType);
    // Insurance capture only applies while check-in is still pending AND no insurance is stored yet.
    // This is the only state in which a chosen insurance type can actually be persisted
    // (via arriveWithInsuranceUpdate); gating the alert/Select/CTA on it avoids a phantom gate
    // for arrived/checked-in/checked-out appointments where the selection would be silently discarded.
    const isInsuranceSelectionRequired = isCheckinPending(checkinStatus) && !appointment.data.insurance_coverage_type;
    // While capturing insurance at check-in, drive the required-documents fetch by the SELECTED type (before
    // it is persisted) so the forms shown — and generated for check-in — match the chosen insurance.
    const insuranceTypeForForms = isInsuranceSelectionRequired ? selectedInsuranceType : undefined;

    const {
        formatted_forms_for_appointment: formattedFormsForAppointment,
        professional_form_templates: professionalFormTemplates,
        is_loading_forms: isLoadingForms,
        form_data_code: formDataCode,
        showQrCode,
        resolveForms,
    } = usePatientAppointmentForms(appointment, { insuranceType: insuranceTypeForForms });

    const isRoomSelectionDisabled = isCheckedOut(checkoutStatus) || !isToday(appointment.day);
    const selectedRoom = resolveSelectedRoom(selectedRoomOverride, roomsSelection, isRoomSelectionDisabled);
    const isFormsDisabled =
        appointmentsCheckedIn[appointment.id]?.is_checking_in ||
        isCheckedOut(checkoutStatus) ||
        !professionalFormTemplates ||
        professionalFormTemplates.length === 0;

    const handleRoomChange = (room_id: string | null): void => {
        setSelectedRoomOverride(room_id ?? '');
        setShowRoomError(false);
    };

    const handleCta = async (): Promise<void> => {
        if (isCheckedOut(checkoutStatus)) {
            // Follow-up appointment
            const practiceId =
                enrichedPractitionerData?.practitioners.find(({ id }) => id === appointment.professional.id)
                    ?.practices[0]?.pvs?.id ?? '';

            if (!appointment.patient) {
                logger.error('Missing patient data for appointment', { appointment: appointment });
                return;
            }

            openAppointmentScheduler({
                mode: 'book',
                patient: patient,
                prefilled_values: {
                    pvs_provider: appointment.pvs_provider,
                    practice_doc_cirrus_id: practiceId,
                    insurance: getInsuranceByKey(appointment.data.insurance_coverage_type),
                    calendar_pvs_id: getCalendarId(appointment),
                },
            });
            return;
        }

        if (!selectedRoom) {
            setShowRoomError(true);
            return;
        }
        setShowRoomError(false);

        const callbacks: CheckInActionCallbacks = {
            onRoomChanged: (room) => updateRoom(room),
            onInsuranceUpdated: handleInsuranceUpdated,
        };

        if (isCheckedIn(checkinStatus) && selectedRoom !== getRoomId(appointment)) {
            await checkInActions.changeRoom({ appointment: appointment, room: selectedRoom }, callbacks);
            return;
        }

        if (isCheckedIn(checkinStatus)) {
            await checkInActions.checkOut({ appointment: appointment, checkinData: apptCheckinData });
            return;
        }

        if (isInsuranceSelectionRequired && selectedInsuranceType) {
            await checkInActions.arriveWithInsuranceUpdate(
                {
                    appointment: appointment,
                    room: selectedRoom,
                    insuranceType: selectedInsuranceType,
                    checkinData: apptCheckinData,
                    enrichedPractitionerData: enrichedPractitionerData,
                },
                callbacks
            );
            return;
        }

        if (isCheckinPending(checkinStatus) && formattedFormsForAppointment?.length === 0) {
            await checkInActions.arriveAndCheckIn(
                {
                    appointment: appointment,
                    room: selectedRoom,
                    checkinData: apptCheckinData,
                    enrichedPractitionerData: enrichedPractitionerData,
                    forms: formattedFormsForAppointment,
                },
                callbacks
            );
            return;
        }

        if (isCheckinPending(checkinStatus)) {
            // Re-resolve forms if unknown (e.g. templates request errored) so the check-in code is still generated.
            const formsForCheckin = formattedFormsForAppointment ?? (await resolveForms());

            await checkInActions.arriveWithForms(
                {
                    appointment: appointment,
                    room: selectedRoom,
                    enrichedPractitionerData: enrichedPractitionerData,
                    forms: formsForCheckin,
                },
                callbacks
            );
            return;
        }

        if (isCheckinArrived(checkinStatus)) {
            await checkInActions.confirmArrived(
                { appointment: appointment, room: selectedRoom, checkinData: apptCheckinData },
                callbacks
            );
            return;
        }
    };

    const getCtaText = (): string => {
        if (isCheckedOut(checkoutStatus)) {
            return 'Folgetermin hinzufügen';
        }

        if (
            isCheckinArrived(checkinStatus) ||
            (isCheckinPending(checkinStatus) && !hasFormsToFill(formattedFormsForAppointment))
        ) {
            return 'Patient:in bestätigen'; // Actual 'Checked in' status
        }

        if (isCheckinPending(checkinStatus)) {
            return `Patient:in ${isConfigAutoCheckin ? '' : 'manuell '}aufnehmen`; // Mark as arrived
        }

        if (isCheckedIn(checkinStatus) && selectedRoom !== getRoomId(appointment)) {
            return 'Zimmer wechseln'; // "Change room"
        }

        if (isCheckedIn(checkinStatus)) {
            return 'Patient:in entlassen'; // "Checkout
        }

        return 'Folgetermin hinzufügen';
    };

    const isUploadDisabled = !isEternoAppointment(appointment) || isInsuranceSelectionRequired;

    const getUploadTooltip = (): string | undefined => {
        if (!isUploadDisabled) return undefined;
        if (!isEternoAppointment(appointment)) return 'Upload für externe Termine nicht verfügbar';
        return 'Bitte Versicherungsart für diesen Termin auswählen, um fortzufahren';
    };

    const handleUploadDocument = (): void => {
        const userId = getUnregisteredUserId(appointment);
        openFileUploadModal({
            userId: userId,
            appointmentId: appointment.id,
            instanceId: getInstanceId(appointment),
            onUploadSuccess: () => mutateAppointmentsForDate(),
        });
    };

    return (
        <div className="flex flex-col gap-4 transition-all duration-500 ease-in-out">
            {isInsuranceSelectionRequired && (
                <>
                    {isMissingInsurance && <InsuranceMissingAlert />}
                    <Select
                        label="Versicherungsart"
                        placeholder="Versicherungsart wählen"
                        data={allowedInsuranceOptions}
                        value={selectedInsuranceType}
                        description="Auswahl gemäß zulässiger Versicherungsarten des Termintyps."
                        onChange={(value) => setDefaultSelectedInsuranceType(value as InsuranceType | undefined)}
                        error={isMissingInsurance}
                    />
                </>
            )}
            {/* TODO: Consider adding a switch to toggle visibility of all rooms */}
            <Select
                disabled={isRoomSelectionDisabled}
                label="Zimmer"
                placeholder="Zimmer wählen"
                data={roomsSelection}
                value={selectedRoom}
                className="w-full"
                onChange={handleRoomChange}
                error={showRoomError ? 'Bitte wählen Sie ein Zimmer aus' : undefined}
            />
            <PatientAppointmentForms
                disabled={isFormsDisabled}
                appointment={appointment}
                insuranceType={insuranceTypeForForms}
                isEditingFormsCallback={(is_editing_form) => setShowCheckinButtons(!is_editing_form)}
            />
            {showCheckinButtons && (
                <div className="flex w-full flex-col gap-5">
                    {isPatientCallSystemActive && isCheckinPending(checkinStatus) && (
                        <div className="flex items-center gap-2">
                            <Switch
                                disabled={appointmentsCheckedIn[appointment.id]?.is_checking_in}
                                checked={isConfigAutoCheckin}
                                onChange={() => setConfigAutoCheckin(!isConfigAutoCheckin)}
                                size="md"
                                label="Auto-Check-In"
                            />
                        </div>
                    )}
                    <div className="flex w-full justify-between gap-2">
                        <ConditionalTooltip
                            label={selectedRoom ? undefined : 'Bitte wählen Sie einen Raum zum Einchecken aus.'}
                        >
                            <Button
                                className="w-full"
                                disabled={
                                    (isInsuranceSelectionRequired && isMissingInsurance) ||
                                    !selectedRoom ||
                                    appointmentsCheckedIn[appointment.id]?.is_checking_in
                                }
                                loading={
                                    appointmentsCheckedIn[appointment.id]?.is_checking_in ||
                                    isLoadingForms ||
                                    isLoadingPrac
                                }
                                onClick={handleCta}
                            >
                                {getCtaText()}
                            </Button>
                        </ConditionalTooltip>
                    </div>
                </div>
            )}
            <Divider className="w-full" />
            <section className="flex w-full gap-4">
                <div className="flex flex-1 justify-center">
                    <IconButton
                        icon={FileUp}
                        text="Dokument hochladen"
                        onClick={handleUploadDocument}
                        disabled={isUploadDisabled}
                        tooltip={getUploadTooltip()}
                    />
                </div>
                {formDataCode?.code && (
                    <div className="flex flex-1 flex-col items-center justify-center">
                        <span className="text-xl font-semibold tracking-[0.2em] text-black">{formDataCode.code}</span>
                        <Paragraph className="text-slate-500">PIN</Paragraph>
                    </div>
                )}
                <div className="flex flex-1 justify-center">
                    <IconButton
                        disabled={!formDataCode}
                        loading={isLoadingForms}
                        onClick={showQrCode}
                        icon={QrCode}
                        text="QR-Code anzeigen"
                        tooltip={formDataCode ? '' : 'Kein QR-Code verfügbar'}
                    />
                </div>
                <AppointmentCommunicationAction appointment={appointment} />
            </section>
            <Divider className="w-full" />
            <DocumentExchange appointment={appointment} />
        </div>
    );
};
